import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import Login from './pages/Login';
import Users from './pages/Users';
import Clients from './pages/Clients';
import Products from './pages/Products';
import SectionProducts from './pages/SectionProducts';
import SectionClients from './pages/SectionClients';
import NewProduct from './pages/NewProduct';
import Settings from './pages/Settings';
import SelectClient from './pages/SelectClient';
import NewOrder from './pages/NewOrder';
import ReviewOrder from './pages/ReviewOrder';
import Production from './pages/Production';
import ProductionDetail from './pages/ProductionDetail';
import DeliveryConfirmation from './pages/DeliveryConfirmation';
import BillingConfirmation from './pages/BillingConfirmation';
import LandingPage from './pages/LandingPage';
import TripsPage from './pages/TripsPage';
import TripCreatePage from './pages/TripCreatePage';
import TripDetailPage from './pages/TripDetailPage';
import PrivateRoute from './components/PrivateRoute';
import AssistantHub from './components/AssistantHub';
import Dashboard from './pages/Dashboard';
import ClientAccount from './pages/ClientAccount';
import OrderList from './pages/OrderList';
import AnalyticsService from './services/analytics';
import usePageTracking from './hooks/usePageTracking';

const AnalyticsTracker = () => {
  useEffect(() => {
    AnalyticsService.initialize();
  }, []);

  usePageTracking();
  return null;
};


const theme = createTheme({
  palette: {
    primary: {
      main: '#800020', // Bordo
      light: '#a00030',
      dark: '#600010',
    },
    secondary: {
      main: '#2e7d32', // Verde
      light: '#4caf50',
      dark: '#1b5e20',
    },
    background: {
      default: '#ffffff', // Blanco
      paper: '#ffffff',
    },
    text: {
      primary: '#0e141b',
      secondary: '#4e7397',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 900,
      letterSpacing: '-0.033em',
    },
    h4: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '0.5rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AnalyticsTracker />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <Users />
                </PrivateRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <PrivateRoute>
                  <Clients />
                </PrivateRoute>
              }
            />

            <Route
              path="/clients/:id/account"
              element={
                <PrivateRoute>
                  <ClientAccount />
                </PrivateRoute>
              }
            />
            <Route
              path="/products"
              element={
                <PrivateRoute>
                  <Products />
                </PrivateRoute>
              }
            />

            <Route
              path="/products/:id/edit"
              element={
                <PrivateRoute>
                  <NewProduct />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/public"
              element={
                <PrivateRoute>
                  <NewOrder orderType="public" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/public/list"
              element={
                <PrivateRoute>
                  <OrderList variant="retail" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/public/products"
              element={
                <PrivateRoute>
                  <SectionProducts variant="retail" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/public/clients"
              element={
                <PrivateRoute>
                  <SectionClients variant="retail" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/public/select-client"
              element={
                <PrivateRoute>
                  <SelectClient orderType="public" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/business"
              element={
                <PrivateRoute>
                  <NewOrder orderType="business" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/business/list"
              element={
                <PrivateRoute>
                  <OrderList variant="wholesale" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/business/products"
              element={
                <PrivateRoute>
                  <SectionProducts variant="wholesale" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/business/clients"
              element={
                <PrivateRoute>
                  <SectionClients variant="wholesale" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/business/select-client"
              element={
                <PrivateRoute>
                  <SelectClient orderType="business" />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/:id/review"
              element={
                <PrivateRoute>
                  <ReviewOrder />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/:id/edit"
              element={
                <PrivateRoute>
                  <NewOrder />
                </PrivateRoute>
              }
            />
            <Route path="/orders" element={<Navigate to="/production" replace />} />
            <Route
              path="/production"
              element={
                <PrivateRoute>
                  <Production />
                </PrivateRoute>
              }
            />
            <Route
              path="/production/:id"
              element={
                <PrivateRoute>
                  <ProductionDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/billing/:id"
              element={
                <PrivateRoute>
                  <BillingConfirmation />
                </PrivateRoute>
              }
            />
            <Route
              path="/delivery/:id"
              element={
                <PrivateRoute>
                  <DeliveryConfirmation />
                </PrivateRoute>
              }
            />
            <Route
              path="/trips"
              element={
                <PrivateRoute>
                  <TripsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/trips/create"
              element={
                <PrivateRoute>
                  <TripCreatePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/trips/:id"
              element={
                <PrivateRoute>
                  <TripDetailPage />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<LandingPage />} />
          </Routes>
          <AssistantHub />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

